import {
  addCents,
  copyExpenses,
  copyMaterialOrder,
  copyWorkOrder,
  documentTotal,
  formatCents,
  toCents,
  type Estimate,
  type Job,
  estimateOptionLabel,
  estimateOptionLabels,
  crewLabel,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchEstimateChildren, insertRow } from "../../lib/queries";
import { useSession } from "../../session";

/**
 * Copying a work order, a material order or the other expenses off one of the
 * job's other estimates.
 *
 * Jonathon, 2026-08-24: "there should be a copy work order from other estimate
 * button beside the work order. same for material order and other expenses."
 *
 * Pricing one roof three ways means building the same order three times, and
 * the second and third are the same order with a different shingle on top. The
 * mirror already keeps the three in step once they exist (see
 * `features/estimate/mirror.ts`); this is the step before that, where the
 * second estimate has nothing on it at all.
 *
 * What comes across, and what is deliberately left behind, is decided by
 * `copyOrder.ts` in packages/core, where it is tested. In one line: everything
 * about what the order *is*, and nothing about what has *happened* to it. A
 * material order copied while still marked paid would be a cost the ladder
 * believes has already left the company.
 *
 * Only this job's own estimates are offered. An order from another customer's
 * job is priced off another customer's roof, and there is no reading of "copy
 * that one" that ends in the right quantities.
 */
export type CopyBucket = "material" | "work" | "expenses";

const NOUN: Record<CopyBucket, string> = {
  material: "Material Order",
  work: "Work Order",
  expenses: "Other Expenses",
};

/** One thing that could be copied, as the list draws it. */
interface Candidate {
  key: string;
  estimateName: string;
  label: string;
  detail: string;
  lines: number;
  total: string;
  /** Does the writing. Returns an order id where there is one to open. */
  write: () => Promise<string | null>;
}

export function CopyFromEstimateModal({
  job,
  estimate,
  estimates,
  bucket,
  optionLabels,
  onClose,
  onCopied,
}: {
  job: Job;
  /** The estimate being copied *onto*. */
  estimate: Estimate;
  /** Every estimate on this job, this one included. */
  estimates: readonly Estimate[];
  bucket: CopyBucket;
  /** What the builder's tabs call each estimate — `estimateOptionLabels`. */
  optionLabels?: ReadonlyMap<string, string>;
  onClose: () => void;
  /** Reloads the card. Given the new order's id where there is one. */
  onCopied: (orderId: string | null) => Promise<void>;
}) {
  const { profileId } = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const others = estimates.filter((option) => option.id !== estimate.id);

  const target = {
    jobId: job.id,
    estimateId: estimate.id,
    createdBy: profileId,
  };

  /**
   * Every other estimate's children, fetched together.
   *
   * The card on screen holds this estimate's orders and nobody else's — that is
   * deliberate and is what stopped three options showing all three sets of
   * orders and trebling the job's cost. So the other estimates are read here.
   */
  const candidates = useAsync(async (): Promise<Candidate[]> => {
    const rows: Candidate[] = [];

    const fetched = [];
    for (const option of others) {
      fetched.push({
        option,
        children: await fetchEstimateChildren(option.id),
      });
    }
    // What each option is called: its package name, or its first line's
    // (Jonathon, 2026-09-15), made distinct so two that start with the same
    // line can be told apart (review of #254). The card's own labels when it
    // passes them, so the picker says what the tabs say.
    const labels =
      optionLabels ??
      estimateOptionLabels(
        others,
        new Map(fetched.map((f) => [f.option.id, f.children.items])),
      );

    for (const { option, children } of fetched) {
      const name =
        labels.get(option.id) ?? estimateOptionLabel(option, children.items);

      if (bucket === "material") {
        for (const order of children.materialOrders) {
          rows.push({
            key: order.id,
            estimateName: name,
            label: `MO-${order.order_number}`,
            detail: order.supplier?.name ?? t("No supplier"),
            lines: (order.items ?? []).length,
            total: formatCents(documentTotal(order)),
            write: async () => {
              const copy = copyMaterialOrder(order, target);
              const created = await insertRow<{ id: string }>(
                "material_orders",
                copy.order,
              );
              for (const item of copy.items) {
                await insertRow("material_order_items", {
                  ...item,
                  material_order_id: created.id,
                });
              }
              return created.id;
            },
          });
        }
      }

      if (bucket === "work") {
        for (const order of children.workOrders) {
          rows.push({
            key: order.id,
            estimateName: name,
            label: `WO-${order.order_number}`,
            detail: crewLabel(order.crew) ?? t("No crew"),
            lines: (order.items ?? []).filter((i) => !i.is_change_order).length,
            total: formatCents(documentTotal(order)),
            write: async () => {
              const copy = copyWorkOrder(order, target);
              const created = await insertRow<{ id: string }>(
                "work_orders",
                copy.order,
              );
              for (const item of copy.items) {
                await insertRow("work_order_items", {
                  ...item,
                  work_order_id: created.id,
                });
              }
              return created.id;
            },
          });
        }
      }

      if (bucket === "expenses") {
        const rowsToCopy = children.expenses.filter((e) => !e.is_change_order);
        if (rowsToCopy.length === 0) continue;
        rows.push({
          key: option.id,
          estimateName: name,
          label: tn(rowsToCopy.length, "1 expense", "{n} expenses"),
          detail: rowsToCopy
            .map((e) => e.name ?? "—")
            .slice(0, 3)
            .join(", "),
          lines: rowsToCopy.length,
          total: formatCents(
            addCents(...rowsToCopy.map((e) => toCents(e.amount))),
          ),
          // All of them at once. Expenses are a list rather than a document,
          // and copying them one at a time would be four presses to do the
          // thing the button says.
          write: async () => {
            for (const values of copyExpenses(rowsToCopy, target)) {
              await insertRow("job_expenses", values);
            }
            return null;
          },
        });
      }
    }

    return rows;
  }, [
    estimate.id,
    bucket,
    others.map((o) => o.id).join(","),
    JSON.stringify([...(optionLabels ?? [])]),
  ]);

  const rows = candidates.data ?? [];

  async function run(candidate: Candidate) {
    setBusy(candidate.key);
    setError(null);
    try {
      const orderId = await candidate.write();
      await onCopied(orderId);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  return (
    <Modal
      title={t("Copy {noun}", { noun: t(NOUN[bucket]) })}
      subtitle={t(
        "From another estimate on job #{number}. Nothing on the estimate it comes from is changed.",
        { number: job.job_number },
      )}
      onClose={onClose}
      roomy
      actions={
        <button className="btn-ghost" onClick={onClose}>
          {t("Close")}
        </button>
      }
    >
      <ErrorNote error={error} />

      {candidates.loading ? (
        <p className="n">{t("Reading the other estimates…")}</p>
      ) : others.length === 0 ? (
        <p className="n">
          {t(
            "This job has only one estimate, so there is nothing to copy from.",
          )}
        </p>
      ) : rows.length === 0 ? (
        <p className="n">
          {tn(
            others.length,
            "The other estimate has no {noun} to copy.",
            "The other estimates have no {noun} to copy.",
            { noun: t(NOUN[bucket].toLowerCase()) },
          )}
        </p>
      ) : (
        <div className="ewrap">
          <table className="etable">
            <thead>
              <tr>
                <th>{t("From")}</th>
                <th>{bucket === "expenses" ? t("What") : t("Order")}</th>
                <th>
                  {bucket === "expenses" ? t("Includes") : t("Supplier / crew")}
                </th>
                <th className="num">{t("Lines")}</th>
                <th className="num">{t("Total")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td>{row.estimateName}</td>
                  <td>{row.label}</td>
                  <td>{row.detail}</td>
                  <td className="num">{row.lines}</td>
                  <td className="num">{row.total}</td>
                  <td>
                    <button
                      className="addrow"
                      style={{ marginTop: 0 }}
                      disabled={busy !== null}
                      onClick={() => void run(row)}
                    >
                      {busy === row.key ? t("Copying…") : t("Copy")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="n">
        {t(
          "A copy starts at the beginning: not placed, not scheduled, nothing paid and nothing reconciled, whatever the order it came from had got to. Change-order lines are left behind — they were agreed on that estimate.",
        )}
      </p>
    </Modal>
  );
}
